import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import Sidebar from "@/components/Sidebar";
import FloorPlan from "@/components/FloorPlan";
import MenuGrid from "@/components/MenuGrid";
import PaymentModal from "@/components/PaymentModal";
import SplitBillModal from "@/components/SplitBillModal";
import ModifierModal from "@/components/ModifierModal";
import OpenItemModal from "@/components/OpenItemModal";
import PrintableReceipt from "@/components/PrintableReceipt";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SquaresFour, GridFour, Trash, Receipt, CreditCard, GridNine, PaperPlaneTilt, X, Plus, NoteBlank, Lightning, SignOut, ShoppingCart, AddressBook, MagnifyingGlass, DotsThree, CurrencyDollar, Printer, PencilSimple, Crown, Barcode } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { formatApiError } from "@/utils/errorFormat";
import AssignTicketDialog from "@/components/pos/AssignTicketDialog";
import QuickOrderNameDialog from "@/components/pos/QuickOrderNameDialog";
import ManagerOverrideDialog from "@/components/pos/ManagerOverrideDialog";
import RefundDialog from "@/components/pos/RefundDialog";
import BarcodeScanDialog from "@/components/pos/BarcodeScanDialog";
import TicketPanel from "@/components/pos/TicketPanel";
import PosTopBar from "@/components/pos/PosTopBar";
import OrdersView from "@/components/pos/OrdersView";
import ReservationsPanel from "@/components/pos/ReservationsPanel";
import useBarcodeScanner from "@/hooks/useBarcodeScanner";

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return isMobile;
};

const useSwipe = (onSwipeLeft, onSwipeRight, enabled) => {
  const touchStart = useRef(null);
  const touchEnd = useRef(null);
  const minSwipeDistance = 50;

  const onTouchStart = useCallback((e) => {
    if (!enabled) return;
    touchEnd.current = null;
    touchStart.current = e.targetTouches[0].clientX;
  }, [enabled]);

  const onTouchMove = useCallback((e) => {
    if (!enabled) return;
    touchEnd.current = e.targetTouches[0].clientX;
  }, [enabled]);

  const onTouchEnd = useCallback(() => {
    if (!enabled || touchStart.current === null || touchEnd.current === null) return;
    const distance = touchStart.current - touchEnd.current;
    if (Math.abs(distance) >= minSwipeDistance) {
      if (distance > 0) onSwipeLeft();
      else onSwipeRight();
    }
    touchStart.current = null;
    touchEnd.current = null;
  }, [enabled, onSwipeLeft, onSwipeRight]);

  return { onTouchStart, onTouchMove, onTouchEnd };
};


// ============================================================================
//   ReturningGuestChip — surfaces a small loyalty badge whenever the
//   selected customer is also a loyalty member. Lets the server greet
//   repeat guests by tier and shows how close they are to the next perk.
// ============================================================================
const TIER_COLORS = {
  bronze:   { ring: "border-amber-700/40",    bg: "bg-amber-700/10",   text: "text-amber-800",  next: "silver",   threshold: 500 },
  silver:   { ring: "border-slate-400/50",    bg: "bg-slate-400/15",   text: "text-slate-700",  next: "gold",     threshold: 1500 },
  gold:     { ring: "border-yellow-500/50",   bg: "bg-yellow-500/15",  text: "text-yellow-800", next: "platinum", threshold: 5000 },
  platinum: { ring: "border-indigo-500/50",   bg: "bg-indigo-500/15",  text: "text-indigo-800", next: null,       threshold: null },
};

const ReturningGuestChip = ({ member }) => {
  if (!member) return null;
  const tier = (member.tier || "bronze").toLowerCase();
  const palette = TIER_COLORS[tier] || TIER_COLORS.bronze;
  const points = member.points || 0;
  const visits = member.visit_count || 0;
  const remaining = palette.threshold ? Math.max(0, palette.threshold - points) : 0;

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border ${palette.ring} ${palette.bg} ${palette.text} text-[11px] font-medium`}
      title={
        palette.next
          ? `${remaining} pts to ${palette.next} tier`
          : "Top tier — keep up the great hospitality!"
      }
      data-testid="returning-guest-chip"
    >
      <Crown size={12} weight="fill" />
      <span>Returning</span>
      <span className="opacity-60">·</span>
      <span className="capitalize">{tier}</span>
      <span className="opacity-60">·</span>
      <span className="font-mono">{points.toLocaleString()} pts</span>
      <span className="opacity-60">·</span>
      <span>{visits} visit{visits === 1 ? "" : "s"}</span>
      {palette.next && remaining > 0 && remaining <= 200 && (
        <>
          <span className="opacity-60">·</span>
          <span className="font-semibold">{remaining} to {palette.next}</span>
        </>
      )}
    </span>
  );
};


const POSPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const showSidebar = user?.role === "admin" || user?.role === "manager";
  const serverMobile = user?.role === "server" && isMobile;
  const [view, setView] = useState("floor"); // floor, menu, or customers
  const [startupApplied, setStartupApplied] = useState(false);
  const [tables, setTables] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState("Main Floor");
  const [menuItems, setMenuItems] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Ticket panel (slides in from right when active)
  const [ticketOpen, setTicketOpen] = useState(false);
  const [isQuickOrder, setIsQuickOrder] = useState(false);

  // Modals
  const [showPayment, setShowPayment] = useState(false);
  const [showSplitBill, setShowSplitBill] = useState(false);
  const [showModifier, setShowModifier] = useState(false);
  // Ticket item currently being edited via the pencil-icon (as opposed to
  // being freshly added from the menu). When set, ModifierModal opens in
  // edit mode with the existing modifiers/notes pre-loaded.
  const [editingTicketItem, setEditingTicketItem] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [showAssignTable, setShowAssignTable] = useState(false);
  // Prompt Quick Order tickets for a customer name at send-to-kitchen
  // time. The kitchen needs a name to call out ("Order up — Sarah!")
  // even when no table or customer profile is attached to the ticket.
  const [showQuickOrderName, setShowQuickOrderName] = useState(false);
  const [quickOrderNameDraft, setQuickOrderNameDraft] = useState("");
  const [showBarcodeScan, setShowBarcodeScan] = useState(false);
  const [selectedMenuItem, setSelectedMenuItem] = useState(null);
  // Open Food / Open Drink / Gift Card ad-hoc entry modal state.
  const [showOpenItem, setShowOpenItem] = useState(false);
  const [openItemTarget, setOpenItemTarget] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  // When a customer with a phone is selected, look them up in the loyalty
  // database so the ticket can show a "Returning Guest" chip with tier,
  // points and visit count. Refreshes when the selected customer changes.
  const [loyaltyInfo, setLoyaltyInfo] = useState(null);
  useEffect(() => {
    setLoyaltyInfo(null);
    const phone = selectedCustomer?.phone;
    if (!phone) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/loyalty/lookup-phone/${encodeURIComponent(phone)}`);
        if (!cancelled && r.data?.found) setLoyaltyInfo(r.data.customer);
      } catch { /* not a member — no chip */ }
    })();
    return () => { cancelled = true; };
  }, [selectedCustomer?.phone]);
  const [taxRate, setTaxRate] = useState(0.08);
  const [moreSubView, setMoreSubView] = useState("customers"); // customers | tips
  const [tipsData, setTipsData] = useState(null);
  const [ordersSubView, setOrdersSubView] = useState("menu"); // menu | open | online | closed
  const [ordersList, setOrdersList] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showRefund, setShowRefund] = useState(false);
  const [refundPin, setRefundPin] = useState("");
  const [refundReason, setRefundReason] = useState("");
  const [refundAmount, setRefundAmount] = useState("");
  const [refundLoading, setRefundLoading] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [assignTab, setAssignTab] = useState("table"); // table | customer | name
  const [newCustForm, setNewCustForm] = useState({ name: "", phone: "", email: "", enroll_loyalty: false });
  const [custSearchQuery, setCustSearchQuery] = useState("");
  const [ticketName, setTicketName] = useState(""); // free-form name to print on ticket
  const [ticketNameDraft, setTicketNameDraft] = useState("");
  // Manager-override gate for Open Orders (modify / delete)
  const [managerAction, setManagerAction] = useState(null); // {orderId, mode: 'edit' | 'delete'}
  const [managerPin, setManagerPin] = useState("");
  const [managerReason, setManagerReason] = useState("");
  const [managerLoading, setManagerLoading] = useState(false);

  // Reservations dialog (opened from POS top bar)
  const [showReservations, setShowReservations] = useState(false);

  // Swipe gestures for mobile server view
  const handleSwipeLeft = useCallback(() => {
    if (!serverMobile) return;
    if (orderItems.length > 0 && !ticketOpen) {
      setTicketOpen(true);
    } else if (view === "floor") {
      setView("menu");
    }
  }, [serverMobile, orderItems.length, ticketOpen, view]);

  const handleSwipeRight = useCallback(() => {
    if (!serverMobile) return;
    if (ticketOpen) {
      setTicketOpen(false);
    } else if (view === "menu") {
      setView("floor");
    }
  }, [serverMobile, ticketOpen, view]);

  const swipeHandlers = useSwipe(handleSwipeLeft, handleSwipeRight, serverMobile);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [tablesRes, menuRes, roomsRes, customersRes, taxRes, posSettingsRes] = await Promise.all([
        axios.get(`${API}/tables`),
        axios.get(`${API}/menu`),
        axios.get(`${API}/rooms`),
        axios.get(`${API}/customers`).catch(() => ({ data: [] })),
        axios.get(`${API}/tax-rate`).catch(() => ({ data: { tax_rate: 8.0 } })),
        axios.get(`${API}/pos-settings`).catch(() => ({ data: { pos_startup_with_tables: true } }))
      ]);
      // Alphabetize tables so the floor list is scannable when there
      // are many rooms/tables (100+ on a busy weekend). Uses natural sort
      // so "Table 2" comes before "Table 10".
      const sortedTables = [...tablesRes.data].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", undefined, { numeric: true, sensitivity: "base" })
      );
      setTables(sortedTables);
      setMenuItems(menuRes.data);
      setRooms(roomsRes.data);
      setCustomers(customersRes.data);
      setTaxRate((taxRes.data.tax_rate || 8.0) / 100);
      if (roomsRes.data.length > 0 && !roomsRes.data.find(r => r.name === selectedRoom)) {
        setSelectedRoom(roomsRes.data[0].name);
      }
      // Apply startup screen preference once on first load
      if (!startupApplied) {
        const startupWithTables = posSettingsRes.data?.pos_startup_with_tables ?? true;
        setView(startupWithTables ? "floor" : "menu");
        setStartupApplied(true);
      }
      // Kiosk auto-persist: if the CFD is enabled in settings, make sure a
      // popup is alive so the customer's screen shows the idle carousel any
      // time the POS is powered on — not only during checkouts. Ping the
      // BroadcastChannel first; if no CFD tab is alive, open one in idle mode.
      if (posSettingsRes.data?.customer_display_enabled) {
        ensureCFDPopup();
      }
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // Opens a CFD popup in IDLE mode (no order_id) unless one is already alive.
  // Uses a BroadcastChannel ping/pong so cross-tab refresh doesn't spawn
  // duplicates. Failures (popup blocked, etc.) are surfaced quietly — the POS
  // still works, it just doesn't have a customer screen.
  const ensureCFDPopup = async () => {
    // Skip entirely on single-screen setups. The CFD is only useful on
    // a dedicated 2nd monitor facing the customer; on a solo terminal
    // it just steals focus every reload.
    const { hasSecondScreen } = await import("@/utils/screenDetect");
    if (!(await hasSecondScreen())) return;
    // NOTE: must match CHANNEL_NAME in PaymentModal.jsx and
    // CustomerDisplayPage.jsx — otherwise the ping never gets pong'd and
    // the POS keeps spawning duplicate CFD popups.
    const CHANNEL = "crest-customer-display";
    let alive = false;
    try {
      const ch = new BroadcastChannel(CHANNEL);
      alive = await new Promise((res) => {
        let done = false;
        const onMsg = (ev) => {
          if (ev.data?.type === "cfd_alive" && !done) {
            done = true;
            ch.removeEventListener("message", onMsg);
            res(true);
          }
        };
        ch.addEventListener("message", onMsg);
        ch.postMessage({ type: "cfd_ping" });
        setTimeout(() => {
          if (!done) {
            ch.removeEventListener("message", onMsg);
            res(false);
          }
          ch.close();
        }, 600);
      });
    } catch { /* channel unavailable */ }
    if (alive) return;
    // No CFD alive — try to open one in idle mode. Respect any saved
    // pinned geometry so the popup lands on the customer's monitor.
    try {
      let geom = null;
      try {
        geom = JSON.parse(localStorage.getItem("cfd_popup_geometry") || "null");
      } catch { /* ignore */ }
      if (!geom || !Number.isFinite(geom.left)) {
        geom = { left: 0, top: 0, width: screen.availWidth, height: screen.availHeight };
      }
      const features = [
        `width=${geom.width}`, `height=${geom.height}`,
        `left=${geom.left}`, `top=${geom.top}`,
        "menubar=no", "toolbar=no", "location=no", "status=no", "popup=yes",
      ].join(",");
      const win = window.open("/customer-display", "_blank", features);
      if (win) {
        window.__cfd_popup = win;
        // Enforce position on Chrome (features are advisory on some builds).
        const enforce = (attempt = 0) => {
          try {
            if (win.closed) return;
            win.moveTo(geom.left, geom.top);
            win.resizeTo(geom.width, geom.height);
          } catch { /* cross-origin during nav */ }
          if (attempt < 3) setTimeout(() => enforce(attempt + 1), 200);
        };
        enforce();
      }
    } catch { /* popup blocked / permission — silent */ }
  };

  // Table selection from floor plan
  const handleTableSelect = async (table) => {
    setSelectedTable(table);
    if (table.current_order_id) {
      try {
        const response = await axios.get(`${API}/orders/${table.current_order_id}`);
        setCurrentOrder(response.data);
        setOrderItems(response.data.items || []);
        if (!serverMobile) setTicketOpen(true);
        setView("menu");
      } catch (error) {
        setCurrentOrder(null);
        setOrderItems([]);
      }
    } else {
      setCurrentOrder(null);
      setOrderItems([]);
      if (!serverMobile) setTicketOpen(true);
      setView("menu");
    }
  };

  const handleTableMove = async (tableId, position) => {
    try {
      await axios.put(`${API}/tables/${tableId}`, { position });
      setTables(tables.map(t => t.id === tableId ? { ...t, position } : t));
    } catch (error) {
      toast.error("Failed to update table position");
    }
  };

  // Start a new ticket from Menu button (no table assigned yet)
  const handleStartTicket = () => {
    setOrdersSubView("menu");
    setView("menu");
    if (!serverMobile) setTicketOpen(true);
    setIsQuickOrder(false);
  };

  // Quick order mode - bar/walk-up, no table needed at all
  const handleQuickOrder = () => {
    setView("menu");
    if (!serverMobile) setTicketOpen(true);
    setIsQuickOrder(true);
    setSelectedTable(null);
    setCurrentOrder(null);
    setOrderItems([]);
  };

  // Customer lookup - select customer and start order (regular order, needs table)
  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setIsQuickOrder(false);
    setSelectedTable(null);
    setCurrentOrder(null);
    setOrderItems([]);
    setView("menu");
    if (!serverMobile) setTicketOpen(true);
    toast.success(`Order for ${customer.name} — select a table`);
  };

  const filteredCustomers = customers.filter(c =>
    !customerSearch ||
    c.name.toLowerCase().includes(customerSearch.toLowerCase()) ||
    (c.phone || "").includes(customerSearch)
  );

  const fetchTips = async () => {
    try {
      const res = await axios.get(`${API}/tips/my-tips`);
      setTipsData(res.data);
    } catch (e) { /* ignore */ }
  };

  const handleCreateAndAssignCustomer = async () => {
    if (!newCustForm.name.trim()) { toast.error("Name is required"); return; }
    try {
      const { enroll_loyalty, ...payload } = newCustForm;
      const res = await axios.post(`${API}/customers`, payload);
      setSelectedCustomer(res.data);
      setCustomers([...customers, res.data]);

      // Optionally enroll in loyalty program
      if (enroll_loyalty && newCustForm.email.trim()) {
        try {
          await axios.post(`${API}/loyalty/customers`, {
            name: newCustForm.name,
            email: newCustForm.email,
            phone: newCustForm.phone || "",
          });
          toast.success(`${res.data.name} created, assigned, and enrolled in loyalty`);
        } catch (e) {
          const d = e.response?.data?.detail || "";
          if (d.toLowerCase().includes("already exists")) {
            toast.info(`${res.data.name} created & assigned — already a loyalty member`);
          } else {
            toast.warning(`${res.data.name} created & assigned, but loyalty enrollment failed`);
          }
        }
      } else {
        toast.success(`Customer ${res.data.name} created and assigned`);
      }

      setNewCustForm({ name: "", phone: "", email: "", enroll_loyalty: false });
      setShowAssignTable(false);
      setAssignTab("table");
    } catch (err) { toast.error(formatApiError(err, "Failed to create customer")); }
  };

  const handleAssignExistingCustomer = (c) => {
    setSelectedCustomer(c);
    setShowAssignTable(false);
    setAssignTab("table");
    setCustSearchQuery("");
    toast.success(`Customer ${c.name} assigned`);
  };

  const assignFilteredCustomers = customers.filter(c =>
    !custSearchQuery ||
    c.name.toLowerCase().includes(custSearchQuery.toLowerCase()) ||
    (c.phone || "").includes(custSearchQuery)
  );


  const fetchOrdersList = async (filter) => {
    try {
      const res = await axios.get(`${API}/orders`);
      const TERMINAL = ["paid", "refunded", "partial_refund", "cancelled"];
      const isTerminal = (o) => TERMINAL.includes(o.status);
      let filtered = res.data;
      if (filter === "open") filtered = res.data.filter(o => !isTerminal(o));
      else if (filter === "online") filtered = res.data.filter(o => (o.source === "self-order" || o.table_name?.includes("Self")) && !isTerminal(o));
      else if (filter === "closed") filtered = res.data.filter(o => isTerminal(o));
      setOrdersList(filtered);
    } catch (e) { setOrdersList([]); }
  };

  const handleRefund = async () => {
    if (refundPin.length !== 4) { toast.error("Enter 4-digit manager PIN"); return; }
    if (!selectedOrder) return;
    const amount = parseFloat(refundAmount);
    if (isNaN(amount) || amount <= 0) { toast.error("Enter a valid refund amount"); return; }
    if (amount > (selectedOrder.total || 0)) { toast.error("Refund cannot exceed order total"); return; }
    setRefundLoading(true);
    try {
      const res = await axios.post(`${API}/orders/refund`, {
        order_id: selectedOrder.id,
        manager_pin: refundPin,
        refund_amount: amount,
        reason: refundReason
      });
      const label = res.data.is_partial ? "Partial refund" : "Full refund";
      toast.success(`${label} — $${res.data.refund_amount.toFixed(2)} by ${res.data.authorized_by}`);
      setShowRefund(false);
      setRefundPin("");
      setRefundReason("");
      setRefundAmount("");
      setSelectedOrder(null);
      fetchOrdersList("closed");
    } catch (err) {
      toast.error(formatApiError(err, "Refund failed"));
    } finally {
      setRefundLoading(false);
    }
  };

  // Menu item click
  const handleMenuItemClick = useCallback((item) => {
    if (!ticketOpen && !serverMobile) {
      // Auto-open ticket on desktop when clicking menu items
      setTicketOpen(true);
    }
    // Open Food / Open Drink / Gift Card — always prompt for
    // price + description before adding to the order.
    if (item.is_open) {
      setOpenItemTarget(item);
      setShowOpenItem(true);
      return;
    }
    const hasModifiers = (item.modifier_groups && item.modifier_groups.length > 0) || (item.modifiers && item.modifiers.length > 0);
    if (hasModifiers) {
      setSelectedMenuItem(item);
      setShowModifier(true);
    } else {
      addItemToOrder(item, []);
    }
  }, [ticketOpen, serverMobile]);

  // Close handler for the modifier modal. Clears synchronously now that
  // the Dialog exit animation is removed — keeps the perceived close at 0ms.
  const handleCloseModifier = useCallback(() => {
    setShowModifier(false);
    setSelectedMenuItem(null);
  }, []);

  // Resolve a scanned barcode into a menu item and drop it onto the
  // current ticket. Multi-scan increments qty on the existing line
  // (per the "no separate lines for the same SKU" spec). Items with
  // modifier groups skip the qty-merge and open the modifier modal
  // instead — a scanned mixed drink still needs the "large / no ice"
  // question.
  const handleBarcodeScan = useCallback(async (code) => {
    const normalized = (code || "").trim();
    if (!normalized) return;
    try {
      const { data: item } = await axios.get(
        `${API}/menu/by-barcode/${encodeURIComponent(normalized)}`
      );
      if (!item?.available) {
        toast.warning(`${item?.name || "Item"} is currently unavailable`);
        return;
      }
      const hasModifiers =
        (item.modifier_groups && item.modifier_groups.length > 0) ||
        (item.modifiers && item.modifiers.length > 0);
      if (hasModifiers) {
        // Auto-open ticket panel for scan just like a click.
        if (!ticketOpen && !serverMobile) setTicketOpen(true);
        setSelectedMenuItem(item);
        setShowModifier(true);
        return;
      }

      // Try to increment qty on an already-present line with the same
      // menu_item_id, no modifiers, and no custom name. On a live
      // order we PUT; on a still-being-built ticket we mutate local
      // ``orderItems`` so successive scans stack cleanly.
      const findMatch = (lines) =>
        lines.find(
          (l) =>
            l.menu_item_id === item.id &&
            !(l.modifiers && l.modifiers.length) &&
            !l.custom_name
        );

      if (currentOrder) {
        const existing = findMatch(currentOrder.items || []);
        if (existing) {
          const newQty = (existing.quantity || 1) + 1;
          const res = await axios.put(
            `${API}/orders/${currentOrder.id}/items/${existing.id}`,
            { quantity: newQty }
          );
          setCurrentOrder(res.data);
          setOrderItems(res.data.items);
          toast.success(`+1 ${item.name} (×${newQty})`);
          return;
        }
      } else {
        const existing = findMatch(orderItems);
        if (existing) {
          setOrderItems((prev) =>
            prev.map((l) =>
              l === existing ? { ...l, quantity: (l.quantity || 1) + 1 } : l
            )
          );
          toast.success(`+1 ${item.name} (×${(existing.quantity || 1) + 1})`);
          return;
        }
      }

      // Not on the ticket yet — add normally. Auto-open ticket panel.
      if (!ticketOpen && !serverMobile) setTicketOpen(true);
      await addItemToOrder(item, []);
    } catch (err) {
      if (err?.response?.status === 404) {
        toast.error(`Barcode ${normalized} not found`);
      } else {
        toast.error(formatApiError(err, "Scan failed"));
      }
    }
  }, [currentOrder, orderItems, ticketOpen, serverMobile]);

  // Global scanner listener — active whenever POS is mounted. Paused
  // while the explicit scan dialog is open (that dialog handles its
  // own input so we don't double-fire).
  useBarcodeScanner(handleBarcodeScan, { paused: showBarcodeScan });

  // Add item to order. ``opts`` carries the open-item overrides:
  //   { override_price, custom_name, gift_card_code }
  const addItemToOrder = async (item, modifiers = [], notes = "", opts = {}) => {
    const payloadItem = {
      menu_item_id: item.id,
      quantity: 1,
      modifiers,
      notes,
      ...(opts.override_price !== undefined ? { override_price: opts.override_price } : {}),
      ...(opts.custom_name ? { custom_name: opts.custom_name } : {}),
      ...(opts.gift_card_code !== undefined ? { gift_card_code: opts.gift_card_code } : {}),
    };
    const effectivePrice = opts.override_price !== undefined ? opts.override_price : item.price;
    const effectiveName = opts.custom_name || item.name;

    if (currentOrder) {
      try {
        const response = await axios.post(`${API}/orders/${currentOrder.id}/items`, [payloadItem]);
        setCurrentOrder(response.data);
        setOrderItems(response.data.items);
        toast.success(`Added ${effectiveName}`);
      } catch (error) {
        toast.error(`Failed to add item: ${formatApiError(error, "Unknown error")}`);
        console.error("Add item failed", error?.response || error);
      }
    } else if (selectedTable) {
      // Create new order with table
      try {
        const response = await axios.post(`${API}/orders`, {
          table_id: selectedTable.id,
          items: [payloadItem],
          guest_count: 1,
          customer_name: ticketName || null,
        });
        setCurrentOrder(response.data);
        setOrderItems(response.data.items);
        setTables(tables.map(t =>
          t.id === selectedTable.id ? { ...t, status: "ordered", current_order_id: response.data.id } : t
        ));
        setSelectedTable({ ...selectedTable, status: "ordered", current_order_id: response.data.id });
        toast.success(`Order started - Added ${effectiveName}`);
      } catch (error) {
        toast.error(`Failed to create order: ${formatApiError(error, "Unknown error")}`);
        console.error("Create order (with table) failed", error?.response || error);
      }
    } else {
      // No table - add to local ticket (will assign table later)
      const modTotal = modifiers.reduce((s, m) => s + (m.price || 0), 0);
      setOrderItems(prev => [...prev, {
        id: `local-${Date.now()}`,
        menu_item_id: item.id,
        menu_item_name: effectiveName,
        base_price: effectivePrice,
        quantity: 1,
        modifiers,
        notes,
        total_price: (effectivePrice + modTotal) * 1,
        is_open: !!item.is_open,
        open_kind: item.open_kind || null,
        gift_card_code: opts.gift_card_code,
        // Cache the overrides so if the ticket is later assigned to a
        // table (see handleAssignTable) the correct price/name/gift-code
        // survive the round-trip through /api/orders.
        _override_price: opts.override_price,
        _custom_name: opts.custom_name,
      }]);
      toast.success(`Added ${effectiveName}`);
    }
    setShowModifier(false);
    setSelectedMenuItem(null);
    setShowOpenItem(false);
    setOpenItemTarget(null);
  };

  // Assign table to current local ticket
  const handleAssignTable = async (tableId) => {
    const table = tables.find(t => t.id === tableId);
    if (!table) return;

    try {
      const response = await axios.post(`${API}/orders`, {
        table_id: tableId,
        items: orderItems.map(i => ({
          menu_item_id: i.menu_item_id,
          quantity: i.quantity,
          modifiers: i.modifiers || [],
          notes: i.notes || "",
          // Preserve open-item overrides captured locally.
          ...(i._override_price !== undefined ? { override_price: i._override_price } : {}),
          ...(i._custom_name ? { custom_name: i._custom_name } : {}),
          ...(i.gift_card_code !== undefined ? { gift_card_code: i.gift_card_code } : {}),
        })),
        guest_count: 1,
        customer_name: ticketName || null,
      });
      setCurrentOrder(response.data);
      setOrderItems(response.data.items);
      setSelectedTable({ ...table, status: "ordered", current_order_id: response.data.id });
      setTables(tables.map(t => t.id === tableId ? { ...t, status: "ordered", current_order_id: response.data.id } : t));
      setShowAssignTable(false);
      toast.success(`Ticket assigned to ${table.name}`);
    } catch (error) {
      toast.error("Failed to assign table");
    }
  };

  const handleRemoveItem = async (itemId) => {
    // Local-only removal for a draft ticket (no server order yet).
    if (!currentOrder) {
      setOrderItems(orderItems.filter(item => item.id !== itemId));
      toast.info("Item removed");
      return;
    }
    // Saved order — must PATCH the server so subtotal/tax/total recalculate,
    // otherwise PaymentModal opens with the stale total (financial risk).
    try {
      const res = await axios.delete(`${API}/orders/${currentOrder.id}/items/${itemId}`);
      setCurrentOrder(res.data);
      setOrderItems(res.data.items);
      toast.info("Item removed");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to remove item";
      toast.error(msg);
    }
  };

  // ===== EDIT TICKET ITEM =====
  // Opens ModifierModal seeded with the current item's modifiers + notes so
  // a server can fix a misheard order without deleting/re-adding the line.
  const handleEditTicketItem = (item) => {
    const menuItem = menuItems.find(mi => mi.id === item.menu_item_id);
    if (!menuItem) {
      toast.error("This item's menu record is missing — remove and re-add.");
      return;
    }
    setEditingTicketItem({ ...item, _menuItem: menuItem });
    setShowModifier(true);
  };

  // Called by ModifierModal when the user confirms edits.
  const handleConfirmEditItem = async (newModifiers, newNotes) => {
    if (!editingTicketItem) return;
    const target = editingTicketItem;
    setShowModifier(false);
    setEditingTicketItem(null);

    // Draft ticket (no server order yet) — mutate local state.
    if (!currentOrder) {
      const modTotal = newModifiers.reduce((s, m) => s + (m.price || 0), 0);
      setOrderItems(prev => prev.map(i =>
        i.id === target.id
          ? { ...i, modifiers: newModifiers, notes: newNotes, total_price: (i.base_price + modTotal) * i.quantity }
          : i
      ));
      toast.success("Item updated");
      return;
    }

    // Saved order — PUT so backend recomputes subtotal/tax/total.
    try {
      const res = await axios.put(`${API}/orders/${currentOrder.id}/items/${target.id}`, {
        menu_item_id: target.menu_item_id,
        quantity: target.quantity,
        modifiers: newModifiers.map(m => ({ name: m.name, price: m.price || 0 })),
        notes: newNotes || "",
      });
      setCurrentOrder(res.data);
      setOrderItems(res.data.items);
      toast.success("Item updated");
    } catch (err) {
      toast.error(`Failed to update item: ${formatApiError(err, "Unknown error")}`);
    }
  };

  // Helper: after a ticket is sent to kitchen, clear the active ticket
  // and route the user to the "Open Orders" sub-view.
  const moveTicketToOpenOrders = () => {
    setCurrentOrder(null);
    setOrderItems([]);
    setSelectedTable(null);
    setSelectedCustomer(null);
    setTicketName("");
    setTicketNameDraft("");
    setIsQuickOrder(false);
    setTicketOpen(false);
    setView("menu");
    setOrdersSubView("open");
    fetchOrdersList("open");
  };

  // Pure local reset — no backend call. Used when discarding a draft ticket
  // that was never saved to the server.
  const resetActiveTicket = () => {
    setCurrentOrder(null);
    setOrderItems([]);
    setSelectedTable(null);
    setSelectedCustomer(null);
    setTicketName("");
    setTicketNameDraft("");
    setIsQuickOrder(false);
    setTicketOpen(false);
    setView("menu");
  };

  // Delete the currently-active ticket. Two flows:
  //  - draft (no currentOrder yet)  → confirm, then reset local state
  //  - saved on server              → reuse the manager-PIN modal to cancel,
  //                                   then reset local state on success.
  const handleDeleteTicket = () => {
    if (!currentOrder) {
      if (orderItems.length === 0 && !selectedTable && !ticketName && !selectedCustomer) return;
      if (window.confirm("Discard this ticket? Any unsent items will be lost.")) {
        resetActiveTicket();
        toast.info("Ticket discarded");
      }
      return;
    }
    // Saved order — manager PIN required to cancel
    setManagerAction({ orderId: currentOrder.id, mode: "delete", order: currentOrder });
    setManagerPin("");
    setManagerReason("");
  };

  const handleSendToKitchen = async () => {
    if (currentOrder) {
      try {
        await axios.put(`${API}/orders/${currentOrder.id}/status`, { status: "in_progress" });
        toast.success("Order sent to kitchen — moved to Open Orders");
        moveTicketToOpenOrders();
      } catch (error) { toast.error("Failed to send order"); }
      return;
    }

    // No current order yet - create one
    if (orderItems.length === 0) return;

    // Any ticket that has items but NO table, customer, or ticket name
    // must have SOMETHING to call at the pass. We prompt for a first
    // name here rather than trusting the ``isQuickOrder`` flag alone
    // (which can be stale after menu-item interactions). This gives the
    // kitchen a callable name for Quick Order (bar), walk-up counter
    // orders, and any bare-ticket flow.
    if (!selectedTable && !selectedCustomer && !ticketName) {
      setQuickOrderNameDraft("");
      setShowQuickOrderName(true);
      return;
    }

    await createAndSendOrder({ overrideName: null });
  };

  const createAndSendOrder = async ({ overrideName }) => {
    // Treat customer-only or name-only tickets as quick orders (no table).
    // The double-bang is critical: JS ``||`` returns the first truthy
    // operand rather than a boolean, so without it ``treatAsQuick``
    // would become the customer name string ("Sarah"), and Pydantic v2
    // would 422 the whole POST.
    const effectiveName = overrideName ?? ticketName;
    const treatAsQuick = !!(isQuickOrder || (!selectedTable && (selectedCustomer || effectiveName)));
    try {
      const response = await axios.post(`${API}/orders`, {
        table_id: selectedTable?.id || null,
        items: orderItems.map(i => ({
          menu_item_id: i.menu_item_id, quantity: i.quantity,
          modifiers: i.modifiers || [], notes: i.notes || ""
        })),
        guest_count: 1,
        is_quick_order: treatAsQuick,
        customer_name: effectiveName || null,
      });
      if (selectedTable) {
        setTables(tables.map(t => t.id === selectedTable.id ? { ...t, status: "ordered", current_order_id: response.data.id } : t));
      }
      if (selectedCustomer) {
        try { await axios.post(`${API}/customers/${selectedCustomer.id}/link-order/${response.data.id}`); } catch (e) { /* non-critical */ }
      }
      await axios.put(`${API}/orders/${response.data.id}/status`, { status: "in_progress" });
      toast.success("Order sent to kitchen — moved to Open Orders");
      if (overrideName) setTicketName(overrideName);
      moveTicketToOpenOrders();
    } catch (error) {
      toast.error(formatApiError(error, "Failed to create order"));
    }
  };

  const confirmQuickOrderName = async () => {
    const name = quickOrderNameDraft.trim();
    if (!name) {
      toast.warning("Please enter a name for this ticket");
      return;
    }
    setShowQuickOrderName(false);
    await createAndSendOrder({ overrideName: name });
  };

  const handlePaymentComplete = async () => {
    try {
      const orderId = currentOrder?.id;
      if (orderId) {
        // Link order to customer if one is selected
        if (selectedCustomer) {
          try { await axios.post(`${API}/customers/${selectedCustomer.id}/link-order/${orderId}`); } catch (e) { /* non-critical */ }
        }
        const receiptRes = await axios.get(`${API}/receipt/${orderId}`);
        setReceiptData(receiptRes.data);
        setShowReceipt(true);
      }
    } catch (error) { console.error("Failed to fetch receipt:", error); }
    await fetchData();
    setSelectedTable(null);
    setCurrentOrder(null);
    setOrderItems([]);
    setShowPayment(false);
    setTicketOpen(false);
    setIsQuickOrder(false);
    setSelectedCustomer(null);
    setTicketName("");
    setTicketNameDraft("");
    setView("floor");
  };

  const handleSplitBill = async (splits) => {
    try {
      await axios.post(`${API}/orders/${currentOrder.id}/split`, { order_id: currentOrder.id, splits });
      toast.success("Bill split successfully");
      setShowSplitBill(false);
    } catch (error) { toast.error("Failed to split bill"); }
  };

  const handleCloseTicket = () => {
    setSelectedTable(null);
    setCurrentOrder(null);
    setOrderItems([]);
    setTicketOpen(false);
    setIsQuickOrder(false);
    setSelectedCustomer(null);
    setTicketName("");
    setTicketNameDraft("");
    setView("floor");
  };

  // Handle opening payment - create order first if needed

  const handlePrintCustomerCopy = async () => {
    const orderId = currentOrder?.id;
    if (!orderId) { toast.error("Send order to kitchen first"); return; }
    try {
      const res = await axios.get(`${API}/receipt/${orderId}`);
      setReceiptData(res.data);
      setShowReceipt(true);
    } catch (err) { toast.error("Failed to load receipt"); }
  };


  const handleOpenPayment = async () => {
    if (currentOrder) {
      setShowPayment(true);
      return;
    }
    // Need to create order first
    if (orderItems.length === 0) return;
    if (!isQuickOrder && !selectedTable && !selectedCustomer && !ticketName) {
      toast.warning("Please assign this ticket first");
      setShowAssignTable(true);
      return;
    }
    const treatAsQuick = isQuickOrder || (!selectedTable && (selectedCustomer || ticketName));
    try {
      const response = await axios.post(`${API}/orders`, {
        table_id: selectedTable?.id || null,
        items: orderItems.map(i => ({
          menu_item_id: i.menu_item_id, quantity: i.quantity,
          modifiers: i.modifiers || [], notes: i.notes || ""
        })),
        guest_count: 1,
        is_quick_order: treatAsQuick,
        customer_name: ticketName || null,
      });
      setCurrentOrder(response.data);
      setOrderItems(response.data.items);
      if (selectedTable) {
        setTables(tables.map(t => t.id === selectedTable.id ? { ...t, status: "ordered", current_order_id: response.data.id } : t));
      }
      setShowPayment(true);
    } catch (error) {
      toast.error(`Failed to create order: ${formatApiError(error, "Unknown error")}`);
      console.error("Open payment / create order failed", error?.response || error);
    }
  };

  const subtotal = orderItems.reduce((sum, item) => sum + item.total_price, 0);
  const tax = subtotal * taxRate;
  const total = subtotal + tax;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="pos-page" {...(serverMobile ? swipeHandlers : {})}>
      {showSidebar && <Sidebar />}

      <main className={showSidebar ? "main-content" : ""}>
        <div className={`${serverMobile ? "p-3 pb-20" : "p-6"} min-h-screen [transition:margin_150ms_ease-out] ${ticketOpen && !serverMobile ? "mr-80 lg:mr-96" : ""}`}>
          {/* Top Bar */}
          <PosTopBar
            serverMobile={serverMobile}
            showSidebar={showSidebar}
            user={user}
            view={view}
            ordersSubView={ordersSubView}
            moreSubView={moreSubView}
            ticketOpen={ticketOpen}
            selectedTable={selectedTable}
            orderItems={orderItems}
            setView={setView}
            setTicketOpen={setTicketOpen}
            handleStartTicket={handleStartTicket}
            handleQuickOrder={handleQuickOrder}
            setShowBarcodeScan={setShowBarcodeScan}
            setShowReservations={setShowReservations}
            logout={logout}
            navigate={navigate}
          />

          {/* Content */}
          {view === "floor" ? (
            <>
              {rooms.length > 1 && (
                <div className={`flex gap-2 mb-4 overflow-x-auto ${serverMobile ? "pb-1" : ""}`}>
                  {rooms.map(room => (
                    <Button key={room.id} variant={selectedRoom === room.name ? "default" : "outline"} size="sm" onClick={() => setSelectedRoom(room.name)} className={`whitespace-nowrap ${serverMobile ? "text-sm px-3 py-2 h-auto" : ""}`} data-testid={`room-tab-${room.name}`}>
                      {room.name}
                    </Button>
                  ))}
                </div>
              )}
              <FloorPlan
                tables={tables.filter(t => t.room === selectedRoom)}
                selectedTable={selectedTable}
                onTableSelect={handleTableSelect}
                onTableMove={handleTableMove}
                canOverride={user?.role === "admin" || user?.role === "manager"}
                onSetStatus={async (tableId, newStatus) => {
                  try {
                    await axios.put(`${API}/tables/${tableId}`, { status: newStatus });
                    setTables(prev => prev.map(t => t.id === tableId ? { ...t, status: newStatus } : t));
                    toast.success("Table marked as Open");
                  } catch (e) {
                    toast.error(formatApiError(e, "Failed to update table"));
                  }
                }}
                compact={serverMobile}
              />
            </>
          ) : view === "more" ? (
            /* ===== MORE MENU ===== */
            <div data-testid="pos-more-view">
              {/* Sub-tabs */}
              <div className={`flex gap-2 mb-4 ${serverMobile ? "" : "max-w-md"}`}>
                <Button variant={moreSubView === "customers" ? "default" : "outline"} size="sm" className="gap-2" onClick={() => setMoreSubView("customers")} data-testid="more-customers-btn">
                  <AddressBook size={16} /> Customers
                </Button>
                <Button variant={moreSubView === "tips" ? "default" : "outline"} size="sm" className="gap-2" onClick={() => { setMoreSubView("tips"); fetchTips(); }} data-testid="more-tips-btn">
                  <CurrencyDollar size={16} /> My Tips
                </Button>
              </div>

              {moreSubView === "customers" ? (
                /* Customer Lookup */
                <div data-testid="pos-customer-lookup">
                  <div className={`relative ${serverMobile ? "mb-3" : "mb-4 max-w-md"}`}>
                    <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input placeholder="Search by name or phone..." value={customerSearch} onChange={(e) => setCustomerSearch(e.target.value)} className="pl-9" data-testid="pos-customer-search" />
                  </div>
                  {selectedCustomer && (
                    <div className={`bg-primary/5 border border-primary/20 rounded-xl p-3 mb-3 flex items-center justify-between ${serverMobile ? "" : "max-w-md"}`} data-testid="selected-customer-banner">
                      <div className="flex items-center gap-2 flex-wrap">
                        <AddressBook size={18} className="text-primary" weight="fill" />
                        <span className="font-medium text-sm">{selectedCustomer.name}</span>
                        {selectedCustomer.phone && <span className="text-xs text-muted-foreground">{selectedCustomer.phone}</span>}
                        {loyaltyInfo && <ReturningGuestChip member={loyaltyInfo} />}
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => setSelectedCustomer(null)} className="text-xs h-7">Clear</Button>
                    </div>
                  )}
                  <div className={`space-y-2 ${serverMobile ? "" : "max-w-md"}`}>
                    {filteredCustomers.length === 0 ? (
                      <div className="text-center py-12 text-muted-foreground">
                        <AddressBook size={40} className="mx-auto mb-2 opacity-30" />
                        <p className="text-sm">{customerSearch ? "No customers match your search" : "No customers yet"}</p>
                      </div>
                    ) : (
                      filteredCustomers.map(c => (
                        <button key={c.id} onClick={() => handleSelectCustomer(c)} className={`w-full text-left bg-white rounded-xl border border-border p-3 flex items-center gap-3 hover:shadow-sm transition-shadow ${serverMobile ? "active:scale-[0.98]" : ""}`} data-testid={`pos-customer-${c.id}`}>
                          <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
                            <AddressBook size={18} className="text-primary" weight="bold" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{c.name}</p>
                            <p className="text-xs text-muted-foreground">{c.phone || c.email || "No contact"}</p>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="font-mono text-xs">{c.total_orders || 0} orders</p>
                            {c.last_order_items?.length > 0 && (
                              <p className="text-[10px] text-muted-foreground truncate max-w-[100px]">Last: {c.last_order_items[0]?.name}</p>
                            )}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              ) : (
                /* Running Tips */
                <div data-testid="pos-tips-view">
                  {!tipsData ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : (
                    <div className={serverMobile ? "" : "max-w-lg"}>
                      {/* Tips Summary */}
                      <div className="grid grid-cols-3 gap-3 mb-4">
                        <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-center">
                          <p className="text-xs text-green-600">Total Tips</p>
                          <p className="font-mono text-2xl font-medium text-green-800" data-testid="tips-total">${tipsData.total_tips.toFixed(2)}</p>
                        </div>
                        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-center">
                          <p className="text-xs text-blue-600">Orders</p>
                          <p className="font-mono text-2xl font-medium text-blue-800">{tipsData.tip_count}</p>
                        </div>
                        <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 text-center">
                          <p className="text-xs text-orange-600">Avg Tip</p>
                          <p className="font-mono text-2xl font-medium text-orange-800">${tipsData.average_tip.toFixed(2)}</p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-sm text-muted-foreground">{tipsData.user_name} — {tipsData.date}</p>
                        <p className="text-sm font-mono">Sales: ${tipsData.total_sales.toFixed(2)}</p>
                      </div>
                      {/* Tips List */}
                      {tipsData.tips.length === 0 ? (
                        <div className="text-center py-12 text-muted-foreground">
                          <CurrencyDollar size={40} className="mx-auto mb-2 opacity-30" />
                          <p className="text-sm">No tips recorded today</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {tipsData.tips.map((tip, i) => (
                            <div key={i} className="bg-white rounded-xl border border-border p-3 flex items-center justify-between" data-testid={`tip-item-${i}`}>
                              <div>
                                <p className="font-medium text-sm">{tip.table_name}</p>
                                <p className="text-xs text-muted-foreground">{tip.time ? new Date(tip.time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}</p>
                              </div>
                              <div className="text-right">
                                <p className="font-mono text-lg font-medium text-green-700">${tip.tip_amount.toFixed(2)}</p>
                                <p className="text-xs text-muted-foreground">on ${tip.order_total.toFixed(2)}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            /* ===== ORDERS VIEW ===== */
            <OrdersView
              serverMobile={serverMobile}
              ordersSubView={ordersSubView} setOrdersSubView={setOrdersSubView}
              fetchOrdersList={fetchOrdersList}
              setTicketOpen={setTicketOpen}
              menuItems={menuItems}
              handleMenuItemClick={handleMenuItemClick}
              selectedOrder={selectedOrder} setSelectedOrder={setSelectedOrder}
              paymentInfo={paymentInfo} setPaymentInfo={setPaymentInfo}
              taxRate={taxRate}
              ordersList={ordersList}
              tables={tables}
              setCurrentOrder={setCurrentOrder}
              setOrderItems={setOrderItems}
              setSelectedTable={setSelectedTable}
              setIsQuickOrder={setIsQuickOrder}
              setTicketName={setTicketName}
              setShowPayment={setShowPayment}
              setShowRefund={setShowRefund}
              setRefundPin={setRefundPin} setRefundReason={setRefundReason} setRefundAmount={setRefundAmount}
              setManagerAction={setManagerAction} setManagerPin={setManagerPin} setManagerReason={setManagerReason}
            />
          )}
        </div>
      </main>

      {/* Ticket Panel — mobile full-screen or desktop right-aside */}
      {ticketOpen && (
        <TicketPanel
          serverMobile={serverMobile}
          state={{
            currentOrder,
            orderItems,
            selectedTable,
            selectedCustomer, setSelectedCustomer,
            ticketName, setTicketName,
            isQuickOrder,
            subtotal, tax, total, taxRate,
          }}
          handlers={{
            setView,
            setTicketOpen,
            setShowAssignTable,
            setShowSplitBill,
            handleCloseTicket,
            handleEditTicketItem,
            handleRemoveItem,
            handleSendToKitchen,
            handleDeleteTicket,
            handlePrintCustomerCopy,
            handleOpenPayment,
          }}
        />
      )}

      {/* Floating cart badge for mobile servers when ticket closed but has items */}
      {serverMobile && !ticketOpen && orderItems.length > 0 && (
        <button
          onClick={() => setTicketOpen(true)}
          className="fixed bottom-4 right-4 z-50 bg-primary text-white rounded-full w-14 h-14 flex items-center justify-center shadow-lg active:scale-95 transition-transform"
          data-testid="mobile-floating-cart"
        >
          <ShoppingCart size={24} weight="fill" />
          <span className="absolute -top-1 -right-1 bg-accent text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">{orderItems.length}</span>
        </button>
      )}

      {/* Assign Ticket Dialog */}
      <AssignTicketDialog
        open={showAssignTable}
        onOpenChange={(o) => {
          setShowAssignTable(o);
          if (!o) {
            setAssignTab("table");
            setCustSearchQuery("");
            setNewCustForm({ name: "", phone: "", email: "", enroll_loyalty: false });
          }
        }}
        state={{
          assignTab, setAssignTab,
          tables,
          selectedCustomer, setSelectedCustomer,
          ticketName,
          ticketNameDraft, setTicketNameDraft,
          setTicketName,
          custSearchQuery, setCustSearchQuery,
          newCustForm, setNewCustForm,
          assignFilteredCustomers,
        }}
        handlers={{
          handleAssignTable,
          handleAssignExistingCustomer,
          handleCreateAndAssignCustomer,
        }}
      />

      {/* Modals */}
      <PaymentModal open={showPayment} onClose={() => setShowPayment(false)} order={currentOrder} onComplete={handlePaymentComplete} />
      <SplitBillModal open={showSplitBill} onClose={() => setShowSplitBill(false)} order={currentOrder} items={orderItems} onSplit={handleSplitBill} />
      <ModifierModal
        open={showModifier}
        onClose={() => { setShowModifier(false); setEditingTicketItem(null); handleCloseModifier(); }}
        item={editingTicketItem ? editingTicketItem._menuItem : selectedMenuItem}
        initialModifiers={editingTicketItem?.modifiers || []}
        initialNotes={editingTicketItem?.notes || ""}
        editMode={!!editingTicketItem}
        onConfirm={(modifiers, notes) => {
          if (editingTicketItem) handleConfirmEditItem(modifiers, notes);
          else addItemToOrder(selectedMenuItem, modifiers, notes);
        }}
      />
      <OpenItemModal
        open={showOpenItem}
        item={openItemTarget}
        onClose={() => { setShowOpenItem(false); setOpenItemTarget(null); }}
        onConfirm={({ price, custom_name, gift_card_code }) =>
          addItemToOrder(openItemTarget, [], "", {
            override_price: price,
            custom_name,
            gift_card_code,
          })
        }
      />
      <PrintableReceipt open={showReceipt} onClose={() => { setShowReceipt(false); setReceiptData(null); }} receipt={receiptData} />

      {/* Quick Order name prompt — collect a customer name so the
          kitchen has something to call when the ticket has no table
          and no linked loyalty customer. */}
      <QuickOrderNameDialog
        open={showQuickOrderName}
        onOpenChange={setShowQuickOrderName}
        value={quickOrderNameDraft}
        onChange={setQuickOrderNameDraft}
        onConfirm={confirmQuickOrderName}
      />

      {/* Explicit barcode scan dialog (POS toolbar → Scan). The
          global useBarcodeScanner hook is paused while this dialog
          owns focus, so scans typed here go through this input. */}
      <BarcodeScanDialog
        open={showBarcodeScan}
        onOpenChange={setShowBarcodeScan}
        onScan={handleBarcodeScan}
      />

      {/* Manager Override Dialog (modify or delete an open order) */}
      <ManagerOverrideDialog
        managerAction={managerAction}
        onClose={() => { setManagerAction(null); setManagerPin(""); setManagerReason(""); }}
        managerPin={managerPin} setManagerPin={setManagerPin}
        managerReason={managerReason} setManagerReason={setManagerReason}
        managerLoading={managerLoading} setManagerLoading={setManagerLoading}
        onCancelSuccess={async (orderId, order) => {
          if (currentOrder?.id === orderId) resetActiveTicket();
          const tableId = order?.table_id;
          if (tableId) {
            setTables(prev => prev.map(t =>
              t.id === tableId ? { ...t, status: "open", current_order_id: null } : t
            ));
          }
          try {
            const tRes = await axios.get(`${API}/tables`);
            setTables(tRes.data);
          } catch (_) { /* local update already applied */ }
          fetchOrdersList(ordersSubView === "online" ? "online" : "open");
        }}
        onEditUnlock={(order) => {
          setCurrentOrder(order);
          setOrderItems(order.items || []);
          if (order.table_id) {
            const t = tables.find(tb => tb.id === order.table_id);
            if (t) setSelectedTable(t);
            setIsQuickOrder(false);
          } else {
            setSelectedTable(null);
            setIsQuickOrder(true);
          }
          if (order.customer_name) setTicketName(order.customer_name);
          setOrdersSubView("menu");
          setTicketOpen(true);
        }}
      />

      {/* Refund Dialog */}
      <RefundDialog
        open={showRefund}
        onOpenChange={(o) => { if (!o) { setShowRefund(false); setRefundPin(""); setRefundReason(""); } }}
        selectedOrder={selectedOrder}
        paymentInfo={paymentInfo}
        refundAmount={refundAmount} setRefundAmount={setRefundAmount}
        refundPin={refundPin} setRefundPin={setRefundPin}
        refundReason={refundReason} setRefundReason={setRefundReason}
        refundLoading={refundLoading}
        onSubmit={handleRefund}
      />

      {/* Reservations quick panel (open from top bar) */}
      <ReservationsPanel
        open={showReservations}
        onOpenChange={setShowReservations}
        tables={tables}
        onTablesChanged={async () => {
          try {
            const r = await axios.get(`${API}/tables`);
            const sorted = [...r.data].sort((a, b) =>
              (a.name || "").localeCompare(b.name || "", undefined, { numeric: true, sensitivity: "base" })
            );
            setTables(sorted);
          } catch (_) { /* non-critical */ }
        }}
      />
    </div>
  );
};

export default POSPage;
